import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import axios from '@tkit/ajax/lib/axios';
import intl from '@/internationalzation/intl';
import { Message } from '@docs/kd-d2c-components';
import { postFileUpload } from '@src/services/walle';
import { FileHelper } from '@src/utils/file';
import { ACCEPT_FILE_TYPES } from '@/common/constants/types';
import {
  IFile,
  LocalFilesHelper,
  UploadStatusEnum,
} from '@src/utils/local-files-helper';
interface UseUploadOptions {
  maxFileCount?: number;
  maxFileSize?: number;
  acceptFileTypes?: string[];
  maxConcurrent?: number;
  canDrag?: boolean;
  isAuditMode?: boolean;
  disabled?: boolean;
  /** 若设置则走该地址 multipart 上传（字段名 file），否则走 walle postFileUpload */
  uploadUrl?: string;
}
interface UseUploadReturn {
  files: IFile[];
  isUploading: boolean;
  deleteFile: (uid: string) => Promise<void>;
  retryUpload: (uid: string) => Promise<void>;
  beforeUpload: (file: IFile) => boolean;
  customRequest: (file: IFile) => Promise<void>;
  setFiles: (files: IFile[]) => void;
  clearFiles: () => void;
  dragProps: {
    onDragOver: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
  };
}
export function useUpload({
  maxFileCount = 10,
  maxFileSize = 100 * 1024 * 1024,
  acceptFileTypes = [...ACCEPT_FILE_TYPES],
  maxConcurrent = 10,
  canDrag = true,
  isAuditMode = false,
  disabled = false,
  uploadUrl,
}: UseUploadOptions): UseUploadReturn {
  // 每个 hook 实例独立的文件存储，避免多个输入框共享同一个全局单例
  const helperRef = useRef(new LocalFilesHelper());
  const helper = helperRef.current;

  const [filesVersion, setFilesVersion] = useState(0);
  const retryFilesMapRef = useRef<Record<string, IFile>>({});
  const uploadQueueRef = useRef<IFile[]>([]);
  const uploadingCountRef = useRef(0);

  // 使用 useMemo 缓存 files 数组，避免不必要的重新渲染
  const files = useMemo(() => {
    return helper.copy();
  }, [filesVersion]);
  const isUploading = files.some(
    (file) => file.status === UploadStatusEnum.UPLOADING,
  );

  // 触发 files 更新的辅助函数
  const triggerFilesUpdate = useCallback(() => {
    setFilesVersion((prev) => prev + 1);
  }, []);
  const deleteFile = useCallback(
    async (uid: string) => {
      delete retryFilesMapRef.current[uid];
      helper.delete(uid);
      triggerFilesUpdate();
    },
    [triggerFilesUpdate],
  );
  const onProgress = useCallback(
    (uid: string, percent: number) => {
      helper.update(uid, {
        percent,
      } as IFile);
      triggerFilesUpdate();
    },
    [triggerFilesUpdate],
  );
  const onComplete = useCallback(() => {
    const completed = helper.files.every(
      (file) => file.status === UploadStatusEnum.SUCCESS,
    );
    return completed;
  }, []);
  const onSuccess = useCallback(
    (file: IFile, data: any) => {
      helper.update(file.uid, {
        status: UploadStatusEnum.SUCCESS,
        percent: 100,
        objectKey: data?.object_key,
        fileUrl: data?.file_url,
        pdfKey: data?.pdf_key,
      } as IFile);
      triggerFilesUpdate();
      delete retryFilesMapRef.current[file.uid];
      onComplete();
    },
    [onComplete, triggerFilesUpdate],
  );
  const onFail = useCallback(
    (file: IFile) => {
      helper.delete(file.uid);
      triggerFilesUpdate();
      delete retryFilesMapRef.current[file.uid];
    },
    [triggerFilesUpdate],
  );
  const processUploadQueue = useCallback(async () => {
    if (
      uploadingCountRef.current >= maxConcurrent ||
      uploadQueueRef.current.length === 0
    ) {
      return;
    }
    const file = uploadQueueRef.current.shift();

    if (!file) return;
    uploadingCountRef.current++;
    try {
      let result: unknown;
      if (uploadUrl) {
        const form = new FormData();
        form.append('file', file as unknown as Blob);
        const { data } = await axios.post(uploadUrl, form, {
          withCredentials: true,
          timeout: 200000,
          onUploadProgress: (evt) => {
            const total = evt.total ?? 0;
            if (total > 0) {
              const percent = ((evt.loaded / total) * 100) || 0;
              onProgress(file.uid, percent);
            }
          },
        });
        result = data;
      } else {
        result = await postFileUpload(
          {
            file: file,
          },
          {
            extra: {
              onUploadProgress: ({
                loaded,
                total,
              }: {
                loaded: number;
                total: number;
              }) => {
                const percent = (loaded / total) * 100 || 0;
                onProgress(file.uid, percent);
              },
              timeout: 200000,
            },
          },
        );
      }

      if (uploadUrl) {
        // 自定义上传接口：{ status: 0, message, data: { key, name, type, size, url } }
        const r = result as any;
        if (r?.status === 0 && r?.data?.url) {
          onSuccess(file, {
            object_key: r.data.key,
            file_url: r.data.url,
          });
        } else {
          onFail(file);
        }
      } else {
        (result as any).code === 0
          ? onSuccess(file, (result as any).data)
          : onFail(file);
      }
    } catch (e) {
      console.error('上传失败:', e);
      onFail(file);
    } finally {
      uploadingCountRef.current--;
      processUploadQueue();
    }
  }, [maxConcurrent, onProgress, onSuccess, onFail, uploadUrl]);
  const onUpload = useCallback(
    (file: IFile) => {
      uploadQueueRef.current.push(file);
      processUploadQueue();
    },
    [processUploadQueue],
  );
  const retryUpload = useCallback(
    async (uid: string) => {
      const file = retryFilesMapRef.current[uid];
      if (!file) return;
      onUpload(file);
    },
    [onUpload],
  );
  const beforeUpload = useCallback(
    (file: IFile): boolean => {
      if (disabled) return false;
      const isSameFileIdentity = (a: IFile, b: IFile) =>
        a.name === b.name &&
        a.size === b.size &&
        a.lastModified === b.lastModified;
      const existingFile = helper.files.find((existing) =>
        isSameFileIdentity(existing, file),
      );
      if (existingFile) {
        Message.error({
          message: intl.t('不能重复上传同一个文件'),
        });
        return false;
      }
      const type = FileHelper.getExtension(file.name);
      if (!acceptFileTypes.includes(type)) {
        Message.error({
          message: intl.t('不支持上传该类型文件'),
        });
        return false;
      }
      if (helper.length >= maxFileCount) {
        Message.error({
          message: intl.t('最多可上传 {maxFileCount} 个文件', {
            maxFileCount: maxFileCount,
          }),
        });
        return false;
      }
      if (file.size > maxFileSize) {
        const sizeMB = Math.round(maxFileSize / (1024 * 1024));
        Message.error({
          message: intl.t('上传失败，文件大小超过 {sizeMB}MB', {
            sizeMB: sizeMB,
          }),
        });
        return false;
      }
      if (file.name.length > 100) {
        Message.error({
          message: intl.t('上传失败，文件名称长度超过 100 个字符'),
        });
        return false;
      }
      helper.add({
        name: file.name,
        size: file.size,
        type: type,
        uid: file.uid,
        status: UploadStatusEnum.UPLOADING,
        percent: 0,
        lastModified: file.lastModified,
        fileType: type,
        originalFile: file,
      } as unknown as IFile);
      triggerFilesUpdate();
      return true;
    },
    [acceptFileTypes, maxFileCount, maxFileSize, triggerFilesUpdate, disabled],
  );
  const customRequest = useCallback(
    async (file: File) => {
      if (!file) return;
      try {
        await onUpload(file as unknown as IFile);
      } catch (error) {
        console.error('上传失败:', error);
      }
    },
    [onUpload],
  );
  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
  }, []);
  const handleDrop = useCallback(
    async (event: DragEvent) => {
      event.preventDefault();
      const fileList = event.dataTransfer?.files as unknown as IFile[];
      if (!fileList || fileList.length === 0) return;
      for (const file of Array.from(fileList)) {
        file.uid =
          Date.now().toString() + Math.random().toString(36).substr(2, 9);
        const canUpload = await beforeUpload(file);
        if (canUpload) {
          onUpload(file);
        }
      }
    },
    [beforeUpload, onUpload],
  );
  useEffect(() => {
    if (!canDrag) return;
    document.body.addEventListener('dragover', handleDragOver);
    document.body.addEventListener('drop', handleDrop);
    return () => {
      document.body.removeEventListener('dragover', handleDragOver);
      document.body.removeEventListener('drop', handleDrop);
    };
  }, [canDrag, handleDragOver, handleDrop]);
  const setFiles = useCallback(
    (newFiles: IFile[]) => {
      helper.clear();
      newFiles.forEach((file) => helper.add(file));
      triggerFilesUpdate();
    },
    [triggerFilesUpdate],
  );
  const clearFiles = useCallback(() => {
    helper.clear();
    setFiles([]);
  }, [triggerFilesUpdate]);
  return {
    files,
    isUploading,
    deleteFile,
    retryUpload,
    beforeUpload,
    customRequest,
    setFiles,
    clearFiles,
    dragProps: {
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
  };
}
